/**
 * Pure helpers for reporting the current browser-level notification state
 * to the user, plus the human-readable labels used by NotificationHelp.vue.
 *
 * Kept pure so we can unit-test the state machine without a browser.
 *
 * Unit tests: cypress/node-tests/notification-status.test.js
 */

export const PERMISSION_STATUSES = {
  granted: { label: 'Allowed', tone: 'ok', icon: 'fa-check-circle' },
  denied:  { label: 'Blocked', tone: 'bad', icon: 'fa-times-circle' },
  default: { label: 'Not asked yet', tone: 'warn', icon: 'fa-question-circle' },
  unsupported: { label: 'Not supported by this browser', tone: 'warn', icon: 'fa-exclamation-circle' },
};

export const SOUND_STATUSES = {
  audible: { label: 'Allowed', tone: 'ok', icon: 'fa-check-circle' },
  muted:   { label: 'Muted for this site', tone: 'bad', icon: 'fa-times-circle' },
  unknown: { label: 'Click "Test sound" to check', tone: 'warn', icon: 'fa-question-circle' },
};

/**
 * Read the current browser notification permission.
 * Pass the `Notification` global explicitly so the function is testable.
 *
 * @param {object|undefined} notificationGlobal  window.Notification (or a mock)
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function readNotificationPermission(notificationGlobal) {
  if (!notificationGlobal) return 'unsupported';
  const p = notificationGlobal.permission;
  if (p === 'granted' || p === 'denied' || p === 'default') return p;
  return 'unsupported';
}

/**
 * Does the user have anything to fix?
 * Used to decide whether to auto-expand the help section by default.
 *
 * @param {'granted'|'denied'|'default'|'unsupported'} permission
 * @param {'audible'|'muted'|'unknown'} sound
 * @returns {boolean}
 */
export function hasActionableProblem(permission, sound) {
  if (permission === 'denied' || permission === 'default') return true;
  if (sound === 'muted') return true;
  return false;
}

/**
 * Human-readable one-line summary of overall status — used in the collapsed
 * header so the user sees at a glance whether anything needs attention.
 *
 * @param {'granted'|'denied'|'default'|'unsupported'} permission
 * @param {'audible'|'muted'|'unknown'} sound
 * @returns {{ text: string, tone: 'ok'|'warn'|'bad' }}
 */
export function summarizeStatus(permission, sound) {
  if (permission === 'unsupported') {
    return { text: 'Your browser does not support desktop notifications', tone: 'warn' };
  }
  if (permission === 'denied') {
    return { text: 'Desktop notifications are blocked — click to fix', tone: 'bad' };
  }
  if (permission === 'default') {
    return { text: 'Desktop notifications are not enabled yet', tone: 'warn' };
  }
  if (sound === 'muted') {
    return { text: 'Sound is muted for this site — click to fix', tone: 'bad' };
  }
  if (sound === 'unknown') {
    return { text: 'Notifications look good — click "Test" to verify', tone: 'warn' };
  }
  return { text: 'All set — notifications and sound are working', tone: 'ok' };
}
